import { SKILLS, type SkillId } from './catalog.ts';
import type { GameState } from './types.ts';

export const SKILL_MILESTONE_LEVELS = [25, 50, 75, 99, 120, 150, 200] as const;

export interface RealmMilestone {
  id: string;
  kind: 'skill' | 'ascension';
  title: string;
  description: string;
  icon: string;
  target: number;
  skill?: SkillId;
}

export const ASCENSION_MILESTONES: readonly RealmMilestone[] = [
  { id: 'ascension:1', kind: 'ascension', title: 'First Echo', description: 'The permanent Ascension Constellation is now within reach.', icon: '✦', target: 1 },
  { id: 'ascension:3', kind: 'ascension', title: 'Doctrine Chamber', description: 'Choose one powerful Doctrine for every future run.', icon: '🜂', target: 3 },
  { id: 'ascension:5', kind: 'ascension', title: 'Four Paths', description: 'The first route keystones and advanced rank stars are within reach.', icon: '✣', target: 5 },
  { id: 'ascension:6', kind: 'ascension', title: 'Fateful Resonance', description: 'The Prosperity keystone can now be awakened after proving your fortune.', icon: '⚡', target: 6 },
  { id: 'ascension:8', kind: 'ascension', title: 'Veilwalker II', description: 'Beyond the Veil rank II can now be purchased.', icon: '🌌', target: 8 },
  { id: 'ascension:10', kind: 'ascension', title: 'Echo Workforce', description: 'The Time keystone and Twin Echoes rank II are now within reach.', icon: '👥', target: 10 },
  { id: 'ascension:11', kind: 'ascension', title: 'Veilwalker III', description: 'Beyond the Veil rank III can now be purchased.', icon: '🌌', target: 11 },
  { id: 'ascension:14', kind: 'ascension', title: 'Veilwalker IV', description: 'Beyond the Veil rank IV can now be purchased.', icon: '🌌', target: 14 },
  { id: 'ascension:15', kind: 'ascension', title: 'Convergence', description: 'Cross-route stars and the final Twin Echoes rank are now within reach.', icon: '☄️', target: 15 },
  { id: 'ascension:17', kind: 'ascension', title: 'Veilwalker V', description: 'Beyond the Veil rank V can now be purchased.', icon: '🌌', target: 17 },
  { id: 'ascension:20', kind: 'ascension', title: 'Veilwalker VI', description: 'Beyond the Veil rank VI can now be purchased.', icon: '🌌', target: 20 },
  { id: 'ascension:23', kind: 'ascension', title: 'Veilwalker VII', description: 'Beyond the Veil rank VII can now be purchased.', icon: '🌌', target: 23 },
  { id: 'ascension:26', kind: 'ascension', title: 'Beyond All Veils', description: 'Grand Convergence and the final Beyond the Veil rank are now within reach.', icon: '🌠', target: 26 }
];

export function skillMilestone(skill: SkillId, level: number): RealmMilestone {
  return {
    id: `skill:${skill}:${level}`,
    kind: 'skill',
    title: `${skill} ${level}`,
    description: level === 200 ? `You mastered ${skill}.` : `You reached level ${level} in ${skill}.`,
    icon: level === 200 ? '👑' : level >= 120 ? '◆' : '🏆',
    target: level,
    skill
  };
}

export function allRealmMilestones(): RealmMilestone[] {
  return [
    ...SKILLS.flatMap((skill) => SKILL_MILESTONE_LEVELS.map((level) => skillMilestone(skill, level))),
    ...ASCENSION_MILESTONES
  ];
}

export function reachedMilestones(state: GameState): RealmMilestone[] {
  const reachedSkills = SKILLS.flatMap((skill) => SKILL_MILESTONE_LEVELS
    .filter((level) => state.run.skills[skill].level >= level)
    .map((level) => skillMilestone(skill, level)));
  return [...reachedSkills, ...ASCENSION_MILESTONES.filter((milestone) => state.prestige.ascensions >= milestone.target)];
}

export function recordReachedMilestones(state: GameState): { state: GameState; unlocked: RealmMilestone[] } {
  const existing = new Set(state.prestige.milestones ?? []);
  const unlocked = reachedMilestones(state).filter((milestone) => !existing.has(milestone.id));
  if (unlocked.length === 0 && state.prestige.milestones) return { state, unlocked };
  for (const milestone of unlocked) existing.add(milestone.id);
  return { unlocked, state: { ...state, prestige: { ...state.prestige, milestones: [...existing] } } };
}
